import React, { useEffect, useState, useRef } from "react";
import { motion, useAnimation, useMotionValue } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";

import "swiper/css";
import "swiper/css/effect-fade";
import { Autoplay, EffectFade } from "swiper/modules";
import { useTranslation } from "react-i18next";
// --- Data ---
const brandLogos = [
  "/images/news-logos/news-logo-1.webp",
  "/images/news-logos/news-logo-13.webp",
  "/images/news-logos/news-logo-2.webp",
  "/images/news-logos/news-logo-12.webp",
  "/images/news-logos/news-logo-3.webp",
  "/images/news-logos/news-logo-11.webp",
  "/images/news-logos/news-logo-4.webp",
  "/images/news-logos/news-logo-10.webp",
  "/images/news-logos/news-logo-5.webp",
  "/images/news-logos/news-logo-9.webp",
  "/images/news-logos/news-logo-6.webp",
  "/images/news-logos/news-logo-7.webp",
  "/images/news-logos/news-logo-8.webp",
];
const decodeHTML = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

const BLOG_BASE_URL = "https://harisharandevgan.com/blog";
const BLOG_API_URL = `${BLOG_BASE_URL}/wp-json/wp/v2/posts?per_page=3&_embed`;
const getBlogPostLink = (url) => {
  const { pathname, search, hash } = new URL(url);
  const path = pathname.replace(/^\/blog(?=\/|$)/, "");
  return `${BLOG_BASE_URL}${path}${search}${hash}`;
};

const tabsData = [
  {
    id: "community",
    title: "Community",
    image: {
      src: "/images/harisharan-devgan-at-school-300.webp",
      srcSet: `
      /images/harisharan-devgan-at-school-300.webp 300w,
      /images/harisharan-devgan-at-school-600.webp 600w
    `,
      alt: "best organic farmers in india",
      width: 300,
      height: 220,
    },
    content:
      "Growing up in a farming family, I consistently noticed shortcomings within our agricultural sector, stemming from a lack of knowledge, resources, and technical expertise among fellow farmers. In 2000, when I embarked on organic farming, my commitment was to bridge these gaps and uplift the farming community. My innovative approach combines traditional and modern farming methods, inspiring farmers to transition from conventional to organic farming, enhancing their productivity and lifestyles. Offering financial and moral support, I provide them with essential tools and knowledge.",
  }
  ,
  {
    id: "sustainability",
    title: "Sustainability",
    image: {
      src: "/images/harisharan-devgan-farms-300.webp",
      srcSet: `
      /images/harisharan-devgan-farms-300.webp 300w,
      /images/harisharan-devgan-farms-600.webp 600w
    `,
      alt: "best organic farmer leader",
      width: 300,
      height: 220,
    },
    content: "Sustainability transcends being a mere term; it constitutes a comprehensive approach underlying environmental stewardship, social responsibility, & economic viability. My commitment to sustainability revolves around addressing and mitigating the environmental impact of farming, specifically targeting the reduction of carbon footprints on our mother Earth. For this, embracing and promoting natural pursuits is my leading solution toward sustainability. Organic farming resists the use of harmful pesticides and chemicals, optimizes the use of natural resources, generates adequate profits for the farmer, & produces healthier crops while maintaining sustainability. In my innovations in organic farming, I have intricately merged traditional wisdom and farming methods with modern techniques, with a distinct emphasis on mitigating environmental harm to the nature while optimizing yields within the constraints of limited resources.",
  },
  {
    id: "energy",
    title: "Clean Energy",
    image: {
      src: "/images/solar-300.webp",
      srcSet: `
      /images/solar-300.webp 300w,
      /images/solar-600.webp 600w
    `,
      alt: "Solar Plants",
      width: 300,
      height: 220,
    },
    content: "Embracing a visionary approach to clean energy is not just an imperative for our generation; it is our ethical responsibility to safeguard the planet for future inhabitants. With our aim to reduce carbon footprints, we are stepping into the production of solar energy by installing a 500 MW solar park in Karnataka in the first phase. Cannabis, being the largest source of biomass on Earth with a rapid growth span of 3-4 months, presents a promising resource for biofuel production, reducing dependence on fossil fuels and maintaining ecological balance. As we are currently involved in cannabis cultivation, our anticipated plan is to utilize its biomass for ethanol production. We are inclined to inspire future generations to understand the value of preserving our natural resources.",
  },
  {
    id: "cannabis",
    title: "Cannabis",
    image: {
      src: "/images/harisharan-devgan-cannabis-300.webp",
      srcSet: `
      /images/harisharan-devgan-cannabis-300.webp 300w,
      /images/harisharan-devgan-cannabis-600.webp 600w
    `,
      alt: "cannabis",
      width: 300,
      height: 220,
    },
    content: "Operating under the umbrella of Niche Agriculture and Pharmaceuticals Limited, our prime focus is to explore the various uses of the Indian Holy Plant “Cannabis”. Our R&D department is currently developing new varieties of cultivars to obtain crops with desired cannabinoids and other compounds compliant with Government norms. At present, we are in the midst of conducting research and clinical trials on Cannabis-based medicines for chronic diseases, with completion anticipated in the near future. This progressive step aims to contribute to community well-being by offering affordable medications to treat debilitating chronic diseases such as Cancer, Epilepsy, Alzheimer’s, Multiple Sclerosis, Chronic pain, and more. Furthermore, we are exploring the potential of cannabis in various sectors and currently entering the food, skincare, and textiles industries.",
  },
];

// --- Components ---

const BannerSlider = () => {
  const swiperRef = useRef(null);
  const slides = [
    {
      alt: "Harisharan Devgan innovative farmer",
      src: "/images/harisharan-devgan-cskhpkv-palampur-banner.webp",
      srcSet: `
        /images/harisharan-devgan-cskhpkv-palampur-banner.webp 720w,
         /images/harisharan-devgan-cskhpkv-palampur-banner.webp 1024w,
         /images/harisharan-devgan-cskhpkv-palampur-banner.webp 1920w
      `,
      width: 1920,
      height: 1080,
    },
    {
      alt: "Harisharan Devgan Organic farming leader",
      src: "/images/harisharan-devgan-banner-1-1920.avif", // largest default
      srcSet: `
        /images/harisharan-devgan-banner-1-720.avif 720w,
        /images/harisharan-devgan-banner-1-1024.avif 1024w,
        /images/harisharan-devgan-banner-1-1920.avif 1920w
      `,
      width: 1920,
      height: 1080,
    },
    {
      alt: "Harisharan Devgan innovative farmer",
      src: "/images/harisharan-devgan-banner-2-1920.avif",
      srcSet: `
        /images/harisharan-devgan-banner-2-720.avif 720w,
         /images/harisharan-devgan-banner-2-1024.avif 1024w,
         /images/harisharan-devgan-banner-2-1920.avif 1920w
      `,
      width: 1920,
      height: 1080,
    },
  ];

  return (
    <section
  className="w-full h-[44vh] md:h-screen relative pt-20 md:pt-0"
  onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
  onMouseLeave={() => swiperRef.current?.autoplay?.start()}
>
      <Swiper
        modules={[Autoplay, EffectFade]}
        effect="fade"
        loop
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        slidesPerView={1}
        observer={false}
        observeParents={false}
        watchSlidesProgress={false}
        className="w-full h-full"
         onSwiper={(swiper) => (swiperRef.current = swiper)}
      >

        {slides.map((slide, index) => (
          <SwiperSlide key={index} className="w-full h-full">
            <img
              src={slide.src}
              srcSet={slide.srcSet}
              sizes="100vw"               // tells browser image spans full viewport
              alt={slide.alt}
             className="w-full h-full object-cover object-top" // cover for full screen
              loading={index === 0 ? "eager" : "lazy"} // first slide loads immediately
              fetchpriority={index === 0 ? "high" : "auto"} // LCP optimization
              decoding="async"
              width={slide.width}
              height={slide.height}
            />
          </SwiperSlide>
        ))}

      </Swiper>
    </section>
  );
};

const LogoCarousel = () => {
  const controls = useAnimation();
  const x = useMotionValue(0);

  const startAnimation = (fromX) => {
    controls.start({
      x: [fromX, -2000],
      transition: {
        duration: ((-2000 - fromX) / -2000) * 25, // speed consistent
        ease: "linear",
        repeat: Infinity,
      },
    });
  };

  useEffect(() => {
    startAnimation(0);
  }, []);

  return (
    <section className="relative bg-[#efefef] py-12 overflow-hidden z-20">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-[250px_1fr] gap-8 items-center">

        {/* Heading */}
        <div className="border-l-4 border-orange-500 pl-6">
          <p className="text-gray-700 uppercase tracking-widest font-bold">
            In the
          </p>
          <h2 className="text-2xl font-black uppercase italic">
            Press<span className="text-orange-500">.</span>
          </h2>
        </div>

        {/* Carousel */}
        <div className="overflow-hidden">
          <motion.div
            className="flex items-center gap-20 will-change-transform"
            style={{ x }}
            animate={controls}
            onMouseEnter={() => controls.stop()}
            onMouseLeave={() => startAnimation(x.get())} // 🔥 resume from same spot
          >
            {[...brandLogos, ...brandLogos, ...brandLogos].map((logo, index) => (
              <img
                key={index}
                src={logo}
                width="220"
                height="100"
                loading="lazy"
                decoding="async"
                alt="Press Logo"
                className="h-10 w-auto shrink-0 opacity-70 hover:opacity-100 grayscale hover:grayscale-0 transition-all duration-500"
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const StorySection = () => {
  const { t } = useTranslation();

  return (
    <section className="bg-[#efefef] pt-20 pb-16">
      <div className="max-w-[1150px] mx-auto px-5">
        <div className="flex flex-wrap bg-white">
          {/* Left Images */}
          <div className="flex-1 flex flex-col gap-4">
            {/* First Image */}
            <div className="w-[88%] max-w-[648px] rounded-lg">
              <img
                src="/images/harisharan-devgan-600.webp"
                srcSet="
    /images/harisharan-devgan-320.webp 320w,
    /images/harisharan-devgan-480.webp 480w,
    /images/harisharan-devgan-600.webp 600w,
    /images/harisharan-devgan-800.webp 800w
  "
                sizes="(max-width: 640px) 90vw, 648px"
                width={648}
                height={900}
                alt="Harisharan Devgan Founder of Niche Group"
                title="Harisharan Devgan"
                className="w-full h-full object-cover custom-img-1 rounded-md"
                fetchpriority="high"
              />
            </div>
            {/* Second Image */}
            <div className="rounded-lg custom">
              <img
                src="/images/harisharan-devgan-at-his-field-on-his-JCB.webp"
                alt="Harisharan Devgan Organic farming leader "
                title="Harisharan Devgan on JCB"
                loading="lazy"
                width={800}
                height={533}
                className="object-cover custom-img-2 rounded-md"
              />
            </div>
          </div>
          {/* Right Content */}
          <div className="flex-1 min-w-[300px] flex flex-col px-6 pt-8">
            <div className="story-text mb-[50px]">

              <div className="flex items-center mb-2">
                <span className="inline-block w-4 h-4 bg-[#424242] mr-4"></span>
                <span className="text-[14px] font-normal uppercase tracking-[1.8px]">
                  {t("homeStory.about")}
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl font-light leading-tight text-gray-900 mb-6">
                {t("homeStory.heading")}{" "}
                <span className="font-medium">
                  {t("homeStory.headingHighlight")}
                </span>
              </h1>

              <p className="text-lg leading-7 mb-6 text-gray-800 custom">
                {t("homeStory.description")}
              </p>


              <Link
                to="/journey"
                className="inline-block bg-green-600 text-white py-3 px-7 rounded-lg font-bold hover:bg-green-800 transition-colors"
              >
                {t("homeStory.button")}
              </Link>
            </div>

            <div className="rounded-lg">
              <img
                src="/images/harisharan-devgan-with-farming-communities-400.webp"
                srcSet="/images/harisharan-devgan-with-farming-communities-400.webp 400w,/images/harisharan-devgan-with-farming-communities-800.webp 800w"
                sizes="(max-width: 768px) 90vw, 567px"
                width="567"
                height="378"
                loading="lazy"
                decoding="async"
                alt="Harisharan Devgan with Farmers"
                className="custom-img-3 object-cover rounded-md"
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};



const PartnersSection = () => {
  const { t } = useTranslation();

  const partners = [
    {
      key: "partner1",
      img: "/images/company-logos/niche-agriculture.webp",
      link: "https://www.nicheagriculture.com/",
    },
    {
      key: "partner2",
      img: "/images/company-logos/napl.webp",
      link: "https://naplimited.com/",
    },
    {
      key: "partner3",
      img: "/images/company-logos/nibl.webp",
      link: "https://niblimited.com/",
    },
    {
      key: "partner4",
      img: "/images/company-logos/niche-film-farm.webp",
      link: "https://nichefilmfarm.com/",
    },
    {
      key: "partner5",
      img: "/images/company-logos/niche-racing.webp",
      link: "https://nicheracing.com/",
    },
  ];

  return (
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-no-repeat"
        style={{
          backgroundImage: "url('/images/harisharan-devgan-company-banner.webp')",
          backgroundPosition: "-85px -25px",
          backgroundAttachment: "scroll",
        }}
      />
      <div className="absolute inset-0 bg-black opacity-30"></div>
      <div className="relative z-10 container mx-auto flex flex-col text-white">
        <div className="flex justify-center pt-8 md:pt-12">
          <img
            src="/images/company-logos/niche-group.webp" alt="Niche Group" width="400" height="300"
            title="Niche Group"
            className="w-full max-w-xs md:max-w-sm h-auto"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mt-10 pb-16 p-2">
          {partners.map((p, idx) => (
            <a
              key={idx}
              href={p.link}
              target="_blank"
              rel="noreferrer"
              aria-label={t(`partners.${p.key}`)}
              className="block bg-white rounded-lg overflow-hidden shadow-lg text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            >
              <img
                src={p.img}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="w-full aspect-square object-cover"
              />

              <h2 className="py-3 px-2 text-sm md:text-base font-semibold text-gray-900">
                {t(`partners.${p.key}`)}
              </h2>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhoWeAreSection = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0);

  const accordions = [
    {
      title: t("whoWeAre.accordion.vision.title"),
      content: t("whoWeAre.accordion.vision.content"),
    },
    {
      title: t("whoWeAre.accordion.mission.title"),
      content: t("whoWeAre.accordion.mission.content"),
    },
    {
      title: t("whoWeAre.accordion.values.title"),
      content: t("whoWeAre.accordion.values.content"),
    },
  ];

  return (
    <section
      className="py-20 md:py-28 bg-gray-50"
      aria-labelledby="about-us-section-heading"
    >
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* LEFT */}
          <div>
            <div className="flex items-center mb-2">
              <span className="inline-block w-4 h-4 bg-[#424242] mr-4"></span>
              <span className="text-[14px] font-normal uppercase tracking-[1.8px]">
                {t("whoWeAre.about")}
              </span>
            </div>

            {/* ✅ SECTION HEADING (used by aria-labelledby) */}
            <h2
              id="about-us-section-heading"
              className="text-[40px] font-medium uppercase tracking-[2px] leading-tight text-[#000000]"
            >
              {t("whoWeAre.heading")}
            </h2>

            <div className="space-y-0">
              {accordions.map((item, idx) => (
                <div key={idx} className="border-b border-gray-300">
                  <div
                    className="flex items-center font-semibold text-lg cursor-pointer py-4 md:py-8"
                    onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                  >
                    <span className="mr-5 text-2xl text-[#d5c9bc] font-bold">
                      {openIndex === idx ? "−" : "+"}
                    </span>
                    {item.title}
                  </div>
                  <div
                    className={`overflow-hidden transition-opacity duration-300 ${openIndex === idx
                      ? "opacity-100 py-6"
                      : "opacity-0 py-0 h-0"
                      }`}
                  >
                    <p className="text-gray-700 custom">{item.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div>
            <h3 className="text-[22px] font-medium tracking-[2px] text-black mb-4">
              {t("whoWeAre.hello")}
            </h3>

            <div className="flex items-center mb-5">
              <span className="inline-block w-4 h-4 bg-[#424242] mr-4"></span>
              <span className="text-[14px] font-normal uppercase tracking-[1.8px]">
                {t("whoWeAre.ideologyTitle")}
              </span>
            </div>

            <p className="text-lg leading-relaxed mb-10 text-gray-700">
              {t("whoWeAre.ideologyText")}
            </p>

            <h4 className="text-[16px] font-medium tracking-[2px] text-black mb-1 flex items-center">
              {t("whoWeAre.name")}
              <span className="inline-block w-4 h-4 bg-[#424242] ml-4"></span>
            </h4>

            <p className="text-[16px] font-normal tracking-[2px] text-gray-600 mb-10">
              {t("whoWeAre.designation")}
            </p>

            <div className="grid grid-cols-2 gap-8 border border-black/10 p-4 rounded-md items-center">
              <div className="text-center">
                <div
                  className="text-[140px] font-semibold text-transparent leading-[0.9]"
                  style={{ WebkitTextStroke: "1px black" }}
                >
                  80
                </div>
              </div>
              <div>
                <p className="text-lg md:text-xl font-medium text-gray-800 leading-tight">
                  {t("whoWeAre.legacy")}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const CommunityEngagementSection = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("community");
  const activeContent = tabsData.find((t) => t.id === activeTab);
  return (
    <section className="w-full overflow-hidden">

      {/* HERO */}
      <div
        className="bg-cover custom"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/images/Harisharan-Devgan-with-Farmers-Community.webp')",
        }}
      >
        <div className="pt-[70px] md:pt-[100px] pb-[140px] md:pb-[180px]">
          <div className="max-w-[1193px] mx-auto flex flex-col md:flex-row gap-[30px] md:gap-[50px] items-start text-white">

            <div className="flex-1">
              <h2 className="text-[1.8rem] md:text-[2.3rem] font-semibold mb-[10px]">
                {t("community.heading")}
              </h2>

              <div className="flex items-center gap-[10px]">
                <span className="w-[40px] md:w-[60px] h-[2px] bg-[#E47203]" />
                <span className="text-[#E47203] text-[14px]">◆</span>
                <span className="w-[40px] md:w-[60px] h-[2px] bg-[#E47203]" />
              </div>
            </div>

            <div className="flex-1 md:flex-[1.5] text-[1.1rem] font-semibold md:text-[1rem] leading-[1.6] opacity-90">
              <p>
                {t("community.description")}
              </p>

            </div>

          </div>
        </div>
      </div>

      {/* CONTENT BOX */}
      <div className="max-w-[1193px] mx-auto -mt-[80px] md:-mt-[100px] bg-white shadow-[0_20px_40px_rgba(0,0,0,0.2)] relative z-10">

        {/* TABS */}
        <div
          role="tablist"
          className="flex bg-black overflow-x-auto scroll-smooth md:overflow-visible"
        >
          {tabsData.map((tab, index) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-[20px] md:px-[25px] py-[14px] 
        text-[0.75rem] md:text-[0.8rem] 
        font-bold uppercase whitespace-nowrap 
        border-r border-[#333] last:border-r-0 
        transition-all duration-300
        ${activeTab === tab.id
                  ? "bg-white text-[#333] border-t-[4px] border-[#E47203]"
                  : "text-[#E47203] hover:bg-white hover:text-[#333]"
                }`}
            >
              {t(`community.tabs.${tab.id}`)}
            </button>
          ))}
        </div>
        {/* TAB CONTENT */}
        <div className="flex flex-col md:flex-row gap-[20px] md:gap-[30px] p-[20px] md:p-[30px] items-start">

          {/* IMAGE */}
          <img
            src={activeContent.image.src}
            srcSet={activeContent.image.srcSet}
            sizes="(max-width: 768px) 100vw, 300px"
            width={activeContent.image.width}
            height={activeContent.image.height}
            loading="lazy"
            decoding="async"
            alt={activeContent.image.alt || activeContent.title}
            className="w-full md:w-[300px] h-[220px] md:h-auto rounded object-cover tab"
          />

          {/* TEXT */}
          <p className="text-[0.85rem] md:text-[0.9rem] text-[#555] leading-[1.7] md:leading-[1.8]">
            {t(`community.content.${activeContent.id}`)}
          </p>

        </div>
      </div>
    </section>
  );
};

const PodcastSection = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleFirstClick = () => {
      const audio = document.querySelector("audio[data-autoplay]");
      if (audio && audio.muted) {
        audio.muted = false;
        audio.play().catch(() => { });
      }
    };
    document.addEventListener("click", handleFirstClick, { once: true });
    return () => document.removeEventListener("click", handleFirstClick);
  }, []);


  return (
    <section className="bg-[#f4f4f4] flex justify-center mt-12 mb-12">
      <div className="max-w-[1200px] bg-white rounded-[15px] flex items-center shadow-[0_15px_35px_rgba(0,0,0,0.08)] relative overflow-visible md:flex-row flex-col p-8 md:p-0">
        <div className="flex-1 max-w-[700px] relative md:-top-[27px] md:mb-[-55px] md:-left-[28px] z-10">
          <img
            src="/images/organic-talks.webp"
            alt="Organic Talks"
            width="539"
            height="1024"
            className="w-[120%] max-w-none rounded-[30px]"
          />
        </div>
        <div className="flex-[2] text-center px-0 py-0 md:px-10 md:py-10 lg:px-12 lg:py-12">
          <h2 className="text-[2.1rem] font-semibold mb-4">
            {t("podcast.title.part1")}{" "}
            <span className="text-[#E47203]">
              {t("podcast.title.highlight")}
            </span>{" "}
            {t("podcast.title.part2")}
          </h2>
          <p className="text-[0.85rem] text-[#666] mb-6 leading-[1.5]">
            {t("podcast.description")}
          </p>

          <div className="bg-[#fafafa] border border-[#eee] rounded-[12px] p-[12px_16px] mb-4">
            <label className="block text-[1rem] font-bold mb-1 text-[#333]">
              {t("podcast.episode1")}
            </label>

            <audio controls preload="metadata" className="w-full h-[36px]">
              <source src="/audio/Podcast-1-Audio-Politics-1.mp3" type="audio/mpeg" />
              Sorry, your browser does not support this audio file.
            </audio>
          </div>

          <div className="bg-[#fafafa] border border-[#eee] rounded-[12px] p-[12px_16px] mb-6">
            <label className="block text-[1rem] font-bold mb-1 text-[#333]">
              {t("podcast.episode2")}
            </label>

            <audio controls preload="metadata" className="w-full h-[36px]">
              <source src="/audio/2nd-Podcast-Audio-extracted-1.mp3" type="audio/mpeg" />
              Sorry, your browser does not support this audio file.
            </audio>
          </div>


          <div className="mt-6">
            <strong className="text-[0.85rem]">
              {t("podcast.listenOn")}
            </strong>

            <div className="flex justify-center gap-3 mt-3 flex-wrap">
              {[
                {
                  src: "spotify-badge.jpg",
                  alt: "Spotify",
                  href: "https://podcasters.spotify.com/pod/show/organictalks-podcast/episodes/Can-the-government-and-farmers-work-together-to-build-a-better-future-for-Indian-agriculture-e2mbrfg"
                },
                {
                  src: "sound-cloud.png",
                  alt: "SoundCloud",
                  href: "https://soundcloud.com/organictalks-podcast"
                },
                {
                  src: "podomatic.jpg",
                  alt: "Podomatic",
                  href: "https://www.podomatic.com/podcasts/theorganictalkspodcastpodcast/episodes/2024-07-23T06_41_43-07_00"
                },
                {
                  src: "youtube.png",
                  alt: "YouTube",
                  href: "https://www.youtube.com/watch?v=M5VYCUAWLA4"
                },
                {
                  src: "facebook-logo.png",
                  alt: "Facebook",
                  href: "https://www.facebook.com/people/Organic-Talks/61554350222611/"
                },
              ].map((platform, i) => (
                <a
                  key={i}
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[70px] h-[70px] border border-[#ddd] rounded-full flex items-center justify-center bg-white transition-colors hover:border-orange-400"
                >
                  <img
                    src={`/images/${platform.src}`}
                    alt={platform.alt}
                    className="h-[20px] w-auto object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generate srcSet from WP image sizes
  const getSrcSet = (sizes) => {
    if (!sizes) return "";
    return Object.values(sizes)
      .filter((s) => s?.source_url && s?.width)
      .map((s) => `${s.source_url} ${s.width}w`)
      .join(", ");
  };

  useEffect(() => {
    const fetchPosts = (url) => fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Blog request failed: ${response.status}`);
      return response.json();
    });

    fetchPosts(BLOG_API_URL)
      .catch((error) => {
        console.warn("Live blog fetch failed, using cached posts:", error);
        return fetchPosts("/blogs.json");
      })
      .then((data) => {
        const formattedPosts = data.map((post) => {
          const media = post._embedded?.["wp:featuredmedia"]?.[0];
          const sizes = media?.media_details?.sizes;

          return {
            title: decodeHTML(post.title.rendered),
            excerpt: decodeHTML(
              post.excerpt.rendered.replace(/<[^>]+>/g, "")
            ),
            link: getBlogPostLink(post.link),
            img:
              sizes?.medium_large?.source_url ||
              sizes?.large?.source_url ||
              media?.source_url ||
              "https://via.placeholder.com/768x512",
            srcSet: getSrcSet(sizes),
            alt: decodeHTML(
              media?.alt_text || post.title.rendered
            ),
            width: sizes?.medium_large?.width || 768,
            height: sizes?.medium_large?.height || 512,
          };
        });

        setPosts(formattedPosts);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Blog fetch error:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <p className="text-center py-10">
        Loading latest blogs...
      </p>
    );
  }

  return (
    <section className="bg-[#f9f9f9] pt-[70px] pb-4 px-[5%] mb-12">
      <div className="max-w-[1200px] mx-auto mb-10 pl-4">

        {/* Heading */}
        <div className="mb-[30px]">
          <div className="flex items-center text-[14px] font-normal uppercase tracking-[1.8px] mb-2">
            <span className="inline-block w-4 h-4 bg-[#424242] mr-4"></span>
            ARTICLES
          </div>

          <h2 className="text-[32px] font-normal uppercase">
            Latest and <strong className="font-black">Greatest Post</strong>
          </h2>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[25px]">
          {posts.map((post, i) => (
            <a
              key={i}
              href={post.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gray-100 block hover:-translate-y-[3px] transition transform"
            >
              <img
                src={post.img}
                srcSet={post.srcSet}
                sizes="(max-width: 768px) 100vw, 33vw"
                width={post.width}
                height={post.height}
                loading="lazy"              // ✅ all lazy
                decoding="async"
                alt={post.alt}
                className="block aspect-[16/9] w-full rounded-md object-cover"
              />

              <div className="py-[25px] px-2">
                <h3
                  className="text-[18px] font-bold text-[#444] leading-[1.3] mb-3 group-hover:text-black"
                  dangerouslySetInnerHTML={{ __html: post.title }}
                />
                <p className="text-[14px] leading-[1.6] text-gray-600">
                  {post.excerpt.slice(0, 120)}...
                </p>
              </div>
            </a>
          ))}
        </div>

      </div>
    </section>
  );
};

const NewsSection = () => {
  const { t } = useTranslation();
  return (
    <section
      className="relative py-10 bg-cover bg-center bg-no-repeat font-sans"
      style={{ backgroundImage: `url('/images/dot-bg.webp')` }}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-[#F9F9F9] opacity-[0.89] z-1" />

      {/* Content Container */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-4">

        {/* Header */}
        <header className="max-w-[1200px] mx-auto mb-10 pl-4">
          <div className="flex items-center text-[14px] font-normal uppercase tracking-[1.8px] mb-2.5 before:content-[''] before:inline-block before:w-4 before:h-4 before:bg-[#424242] before:mr-4">
            {t("news.label")}
          </div>
          <h3 className="text-[32px] font-bold text-[#333]">
            {t("news.heading")}
          </h3>
        </header>
        {/* Newspaper Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-[1fr_2fr_1fr] gap-8">
          {/* Column 1: Featured & Horizontal (Central in Desktop) */}
          <div className="order-1 md:order-2 flex flex-col">
            {/* Featured Item */}
            <article className="group text-center">
              <a href="https://www.indiatoday.in/amp/education-today/news/story/jpsc-prelims-2025-supreme-court-pil-seeks-exam-cancellation-cbi-probe-2966504-2026-08-08" className="no-underline" target="_Blank">
                <div className="mb-4">
                  <img src="images/jpsc-prelims-2025-supreme-court-pil.webp" alt="JPSC Prelims 2025: PIL in Supreme Court" title="JPSC Prelims 2025: PIL in Supreme Court" width="800" height="533" className="w-full h-auto" />
                </div>
                <h3 className="text-2xl font-bold text-[#333] mb-2 leading-tight">
                  {t("news.featured.title")}
                </h3>
                <p className="text-sm text-gray-600 mb-2.5">
                  {t("news.featured.tease")}
                </p>

                <p className="text-xs italic text-gray-400">
                  {t("news.featured.meta")}
                </p>
              </a>
            </article>

            {/* Horizontal Item */}
            <article className="border-t border-gray-200 pt-2 mt-2">
              <a
                href="#"
                className="flex no-underline group items-start"
              >
                {/* Text Content */}
                <div className="flex-1 pr-5 custom">
                  <h3 className="text-[18px] font-bold text-[#333] mb-2 leading-snug">
                    {t("news.horizontal.title")}
                  </h3>

                  <p className="text-sm text-gray-600 mb-2.5">
                    {t("news.horizontal.tease")}
                  </p>

                  <p className="text-xs italic text-gray-400">
                    {t("news.horizontal.meta")}
                  </p>
                </div>

                {/* Video Section (slightly bigger than image) */}
                <div className="flex-[0_0_231px]">
                  <video
                    src="https://harisharandevgan.com/video/neet-pg.mp4"
                    poster="/images/neet26.jpg"
                    controls
                    preload="metadata"
                    className="w-full h-auto rounded-md shadow-sm"
                  />
                </div>
              </a>
            </article>
          </div>

          {/* Column 2: Side Articles (Left in Desktop) */}
          <div className="order-2 md:order-1 flex flex-col divide-y divide-gray-200">
            <NewsCard
              img="/images/harisharan-devgan-cskhpkv-palampur.webp" alt="किसान आत्मनिर्भर होगा तभी भारत विकसित राष्ट्र बनेगा"
              title={t("news.left.1.title")}
              tease={t("news.left.1.tease")}
              meta={t("news.left.1.meta")}
              link="https://www.hillagric.ac.in/news/news.html"
            />
            <NewsCard
              title={t("news.left2.2.title")}
              tease={t("news.left2.2.tease")}
              meta={t("news.left2.2.meta")}
              link="https://www.newindianexpress.com/nation/2026/Feb/04/sc-issues-notice-to-centre-on-plea-against-reduction-of-neet-pg-cut-off"
            />
            <NewsCard
              title={t("news.left3.3.title")}
              tease={t("news.left3.3.tease")}
              meta={t("news.left3.3.meta")}
              link="https://www.businessworld.in/article/Harisharaan-Devgan-pushing-limits-towards-sustainable-development-agriculture-with-a-new-dimension/25-08-2020-312716/"
            />
          </div>

          {/* Column 3: Side Articles (Right in Desktop) */}
          <div className="order-3 flex flex-col divide-y divide-gray-200">
            <NewsCard
              img="/images/harisharan-devgan-cbi-probe-demand-sc-notice-to-jharkhand-govt-jpsc.webp"
              alt="सीबीआई जांच की मांग पर सुप्रीम कोर्ट का सरकार और जेपीएससी को नोटिस"
              title={t("news.left4.4.title")}
              tease={t("news.left4.4.tease")}
              meta={t("news.left4.4.meta")}
              link="#"
            />
            <NewsCard
              title={t("news.left5.5.title")}
              tease={t("news.left5.5.tease")}
              meta={t("news.left5.5.meta")}
              link="https://krishijagran.com/success-story/harisharan-devgan-perspective-on-water-conservation-and-management/"

            />
            <NewsCard
              title={t("news.left6.6.title")}
              tease={t("news.left6.6.tease")}
              meta={t("news.left6.6.meta")}
              link="https://www.ustimesnow.com/niche-agriculture-limited-harvesting-the-reaps-of-horticulture/"

            />
          </div>

        </div>

        {/* Footer Button */}
        <div className="text-center mt-[50px]">
          <Link
            to="/news"  // replace with your internal page path
            className="inline-block bg-black text-white px-[35px] py-3 rounded-full font-bold text-sm hover:bg-gray-800 transition-colors"
          >
            {t("news.viewAll")}
          </Link>

        </div>
      </div>
    </section>
  );
};

// Reusable Sub-component for smaller news items
const NewsCard = ({ img, title, tease, meta, noImg, link }) => (
  <article className="first:pt-0 last:pb-0">
    <a
      href={link || "#"} // ✅ Ensure link has a valid URL fallback
      target="_blank"
      rel="noopener noreferrer"
      className="no-underline block group pb-2"
    >
      {/* Optional image */}
      {!noImg && img && (
        <div className="mb-4">
          <img
            src={img}
            alt={title} // ✅ Always include alt for SEO
            className="w-full h-auto"
            loading="lazy" // ✅ Lazy-load non-critical images
            decoding="async"
          />
        </div>
      )}

      {/* Title */}
      <h3 className="text-[17px] font-bold text-[#333] mb-2 group-hover:text-blue-900 transition-colors">
        {title}
      </h3>

      {/* Teaser */}
      <p className="text-sm text-gray-600 mb-2">{tease}</p>

      {/* Meta */}
      <p className="text-xs italic text-gray-400">{meta}</p>
    </a>

  </article>
);


const HomeFooterTop = () => {
  const { t } = useTranslation();
  return (
    <section
      className="relative w-full flex items-center overflow-hidden -mt-2"
      style={{
        backgroundImage:
          "url('/images/quote-jpg.webp')",
        backgroundPosition: "0px -247px",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        transition: "background 0.3s, border-radius 0.3s, opacity 0.3s",
        minHeight: "260px",
      }}
    >
      {/* Background Overlay */}
      <div
        className="absolute inset-0 bg-black"
        style={{ opacity: 0.62 }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1320px] mx-auto px-6 text-white flex flex-col text-center justify-center">
        {/* Quote */}
        <h2
          className="text-center md:text-left leading-snug"
          style={{
            fontFamily: '"Noto Serif", sans-serif',
            fontSize: "2.9rem",
            fontWeight: 600,
            letterSpacing: "0.7px",
            color: "#FFFFFF",
            textAlign: "center",
            lineHeight: "80px",
          }}
        >
          {t("footerQuote.line1")}
          <br />
          <span className="italic">
            {t("footerQuote.line2")}
          </span>
        </h2>

        {/* Signature */}
        <div className="flex justify-center md:justify-end mt-4">
          <img
            src="/images/signature-hsd.png"
            alt="Quote Signature"
            width="1024"
            height="240"
            className="h-[38px] md:h-[44px] opacity-90 w-auto"
          />
        </div>
      </div>
    </section>
  );
};
export default function HomePage() {
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Harisharan Devgan | Agriculturist &amp; Public Voice</title>
        <meta
          name="description"
          content="Harisharan Devgan is an agriculturist who believes in social welfare, farmers' interests and rural development, and remains actively involved in public life and politics."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Open Graph / Facebook */}
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Harisharan Devgan | Agriculturist &amp; Public Voice" />
        <meta
          property="og:description"
          content="Harisharan Devgan is an agriculturist who believes in social welfare, farmers' interests and rural development, and remains actively involved in public life and politics."
        />

        <meta property="og:site_name" content="Harisharan Devgan" />
        <meta property="og:image" content="https://harisharandevgan.com/images/harisharan-devgan-800.webp" />
        <meta
          property="og:image:secure_url"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="576" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:alt" content="Harisharan Devgan - Agriculturist with Political Voice" />
        <meta property="og:updated_time" content="2026-02-01T11:53:28+05:30" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harisharan Devgan | Agriculturist &amp; Public Voice" />
        <meta
          name="twitter:description"
          content="Harisharan Devgan is an agriculturist who believes in social welfare, farmers' interests and rural development, and remains actively involved in public life and politics."
        />
        <meta name="twitter:image" content="https://harisharandevgan.com/images/harisharan-devgan-800.webp" />
        <meta name="twitter:label1" content="Written by" />
        <meta name="twitter:data1" content="Harisharan Devgan" />
        <meta name="twitter:label2" content="Time to read" />
        <meta name="twitter:data2" content="6 minutes" />

        {/* Favicon / Icons */}
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon//apple-touch-icon.png" />
        <link rel="manifest" href="/favicon//site.webmanifest" />
        <link rel="shortcut icon" href="/favicon//favicon.ico" />
      </Helmet>
      <SEO />

      {/* --- Page Content --- */}
      <div className="font-sans overflow-x-hidden">
        <BannerSlider />
        <LogoCarousel />
        <StorySection />
        <PartnersSection />
        <WhoWeAreSection />
        <CommunityEngagementSection />
        <PodcastSection />
        <BlogSection />
        <NewsSection />
        <NewsCard />
        <HomeFooterTop />
      </div>
    </>
  );
}
